import { LocalNotifications } from '@capacitor/local-notifications';
import dayjs, { Dayjs } from 'dayjs';
import ActionTypes, { NotificationIds, PERIOD_REMINDER_IDS } from 'notifications';
import { useTranslation } from 'react-i18next';

export default function useSchedulePeriodNotifications() {
  const { t } = useTranslation();

  const cancelPeriodReminders = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    LocalNotifications.getPending().then(pending => {
      const notificationsToCancel = pending.notifications.filter(n => PERIOD_REMINDER_IDS.includes(n.id));
      if (notificationsToCancel.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        LocalNotifications.cancel({
          notifications: notificationsToCancel,
        });
      }
    });
  };

  const schedulePeriodReminder = (nextPeriodLikelyStartDate: Dayjs, areDailyNotificationsEnabled: boolean) => {
    const buildNotification = (text: string, offsetToStart: number, notificationId: number) => {
      return {
        title: t('appName'),
        body: text,
        id: notificationId,
        schedule: {
          at: nextPeriodLikelyStartDate.add(offsetToStart, 'day').set('hour', 21).set('minute', 0).toDate(),
        },
        actionTypeId: ActionTypes.PERIOD_REMINDER,
      };
    };

    const notifications = [
      // period might start in 3 days
      buildNotification(
        t('symptomTracking.periodReminder.notifications.d_minus_3'),
        -3,
        NotificationIds.PERIOD_REMINDER_D_MINUS_3,
      ),
      // period might start tomorrow
      buildNotification(
        t('symptomTracking.periodReminder.notifications.d_minus_1'),
        -1,
        NotificationIds.PERIOD_REMINDER_D_MINUS_1,
      ),
      ...(areDailyNotificationsEnabled
        ? // we don't need post-period reminders since the user already has a daily reminder
          []
        : [
            // did your period start ?
            buildNotification(
              t('symptomTracking.periodReminder.notifications.d_plus_1'),
              1,
              NotificationIds.PERIOD_REMINDER_D_PLUS_1,
            ),
            // don't forget to track your period if it started
            buildNotification(
              t('symptomTracking.periodReminder.notifications.d_plus_4'),
              4,
              NotificationIds.PERIOD_REMINDER_D_PLUS_4,
            ),
            // i'll stop reminding you.
            buildNotification(
              t('symptomTracking.periodReminder.notifications.d_plus_10'),
              10,
              NotificationIds.PERIOD_REMINDER_D_PLUS_10,
            ),
          ]),
    ];

    const notificationsToSchedule = notifications.filter(notification => notification.schedule.at > dayjs().toDate());
    const notificationsToCancel = notifications.filter(notification => notification.schedule.at <= dayjs().toDate());

    if (notificationsToSchedule.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      LocalNotifications.schedule({
        notifications: notificationsToSchedule,
      });

      notificationsToSchedule.forEach(notification => {
        console.log('scheduled notification', notification.schedule.at, notification.body);
      });
    }

    if (notificationsToCancel.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      LocalNotifications.getPending().then(pending => {
        const existingNotifications = pending.notifications.filter(notification =>
          notificationsToCancel.map(toCancel => toCancel.id).includes(notification.id),
        );
        if (existingNotifications.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          LocalNotifications.cancel({
            notifications: existingNotifications,
          });
        }
      });
    }
  };

  return {
    schedulePeriodReminder,
    cancelPeriodReminders,
  };
}
